/**
 * Public getting-started guide for beta testers — the V1.x User Guide (Beta-1 plan P8-01).
 *
 * Route: /guide (no auth — linked from the welcome email and the entry page's "About").
 * Content is `docs/beta/getting-started-guide.md`, imported raw and rendered by
 * `platform/guide/miniMarkdown.js`, so the file is the single source of truth (Tim edits the
 * markdown; the page follows). Section anchors: `/guide#c-learning-the-system` etc.; the
 * onboarding steps deep-link to them.
 */
import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Container, Typography, Paper, Button, Divider, Link, List, ListItem, ListItemText, Chip } from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import guideSource from '../../docs/beta/getting-started-guide.md?raw';
import { parseMarkdown, toc } from '../platform/guide/miniMarkdown.js';

const APP_URL = 'https://app.repsit.ai';

function Inline({ parts }) {
  return parts.map((p, i) => {
    if (p.t === 'bold') return <strong key={i}>{p.v}</strong>;
    if (p.t === 'italic') return <em key={i}>{p.v}</em>;
    if (p.t === 'code') return <Box key={i} component="code" sx={{ px: 0.5, borderRadius: 0.5, bgcolor: 'action.hover', fontFamily: '"Roboto Mono", monospace', fontSize: '0.9em' }}>{p.v}</Box>;
    if (p.t === 'link') return <Link key={i} href={p.href} target="_blank" rel="noopener noreferrer">{p.v}</Link>;
    return <React.Fragment key={i}>{p.v}</React.Fragment>;
  });
}

function Block({ block }) {
  switch (block.type) {
    case 'h':
      if (block.level === 1) return null; // the page header renders the title
      return (
        <Typography
          id={block.id}
          variant={block.level === 2 ? 'h5' : 'h6'}
          component={block.level === 2 ? 'h2' : 'h3'}
          sx={{ fontWeight: 700, mt: block.level === 2 ? 5 : 3, mb: 1.5, scrollMarginTop: 80 }}
        >
          <Inline parts={block.inline} />
        </Typography>
      );
    case 'p':
      return <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.7 }}><Inline parts={block.inline} /></Typography>;
    case 'quote':
      return (
        <Box sx={{ borderLeft: 3, borderColor: 'warning.main', pl: 2, py: 0.5, mb: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary"><Inline parts={block.inline} /></Typography>
        </Box>
      );
    case 'ul':
      return (
        <List dense sx={{ mb: 1.5, pl: 1 }}>
          {block.items.map((parts, i) => (
            <ListItem key={i} sx={{ display: 'list-item', listStyleType: 'disc', ml: 2, py: 0.25 }}>
              <ListItemText primaryTypographyProps={{ variant: 'body1', lineHeight: 1.6 }} primary={<Inline parts={parts} />} />
            </ListItem>
          ))}
        </List>
      );
    case 'hr':
      return <Divider sx={{ my: 3 }} />;
    default:
      return null;
  }
}

export default function Guide() {
  const blocks = useMemo(() => parseMarkdown(guideSource), []);
  const sections = useMemo(() => toc(blocks), [blocks]);
  const { hash } = useLocation();
  useEffect(() => {
    const id = (hash || '').replace('#', '');
    if (!id) return;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [hash, blocks]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 3, md: 6 } }}>
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Box component="img" src="/repsit-logo.png" alt="REPSit" sx={{ height: 88, mb: 1 }} />
          <Typography variant="h3" component="h1" sx={{ fontWeight: 800, mb: 1 }}>
            Beta — Getting Started
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Ten minutes to your first &ldquo;there it is.&rdquo; Everything below happens in DREW, your Drift &amp; Accumulation Early Warning System.
          </Typography>
          <Button variant="contained" size="large" startIcon={<RocketLaunchIcon />} href={APP_URL} sx={{ mt: 2 }}>
            Open REPSit.ai
          </Button>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.75, mt: 2.5 }}>
            {sections.map((s) => (
              <Chip key={s.id} label={s.text} size="small" variant="outlined" component="a" href={`#${s.id}`} clickable />
            ))}
          </Box>
        </Box>

        <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 4 } }}>
          {blocks.map((b, i) => <Block key={i} block={b} />)}
        </Paper>
      </Container>
    </Box>
  );
}
